import React, { useState } from 'react';
import { ArrowLeftIcon, MapPinIcon, DownloadIcon, ShieldCheckIcon, UploadIcon, FileTextIcon, HistoryIcon, CreditCardIcon, GavelIcon, BuildingIcon, AlertCircleIcon, CheckCircleIcon, MapIcon, XIcon, FileIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function PlotDetailsPage({
  plotId,
  navigateTo
}: {
  plotId: string | null;
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [showUploadUI, setShowUploadUI] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState(['Original Deed.pdf', 'Mutation Order.pdf', 'Tax Receipt 2025.pdf']);
  const plot = samplePlots.find(p => p.id === plotId) || samplePlots[0];
  const handleUploadClick = () => {
    setShowUploadUI(true);
    setUploadProgress(0);
  };
  const simulateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileName = e.target.files[0].name;
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setUploadedFiles([fileName, ...uploadedFiles]);
            setShowUploadUI(false);
          }, 500);
        }
      }, 100);
    }
  };
  const tabs = [{
    id: 'overview',
    label: t('tabOverview'),
    icon: FileTextIcon
  }, {
    id: 'clo',
    label: t('tabCLO'),
    icon: ShieldCheckIcon
  }, {
    id: 'mutation',
    label: t('tabMutation'),
    icon: HistoryIcon
  }, {
    id: 'tax',
    label: t('tabTax'),
    icon: CreditCardIcon
  }, {
    id: 'parcelMap',
    label: t('tabParcelMap') || 'Parcel Map',
    icon: MapIcon
  }, {
    id: 'transferHistory',
    label: t('tabTransferHistory') || 'Transfer History',
    icon: HistoryIcon
  }, {
    id: 'disputes',
    label: t('tabDisputes'),
    icon: GavelIcon
  }, {
    id: 'mortgages',
    label: t('tabMortgages'),
    icon: BuildingIcon
  }, {
    id: 'documents',
    label: t('tabDocuments'),
    icon: UploadIcon
  }];
  return <div className="space-y-6 pb-10" data-id="element-1152">
      <button onClick={() => navigateTo('myPlots')} className="flex items-center text-sm font-medium text-gray-500 hover:text-[#006A4E] transition-colors" data-id="element-1153">
        <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1154" /> Back to My Plots
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden" data-id="element-1155">
        {plot.dispute && <div className="absolute top-0 left-0 w-full h-1 bg-red-500" data-id="element-1156"></div>}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-1157">
          <div className="flex items-center space-x-4" data-id="element-1158">
            <div className="w-16 h-16 bg-[#006A4E] rounded-xl flex items-center justify-center shadow-inner flex-shrink-0" data-id="element-1159">
              <MapPinIcon className="w-8 h-8 text-white" data-id="element-1160" />
            </div>
            <div data-id="element-1161">
              <div className="flex items-center space-x-2 mb-1" data-id="element-1162">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-1163">
                  {language === 'bn' ? plot.nameBn : plot.name}
                </h1>
                {plot.dispute && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200" data-id="element-1164">
                    <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1165" />
                    Disputed
                  </span>}
              </div>
              <p className="text-sm text-gray-500 flex items-center" data-id="element-1166">
                <span className="font-medium text-gray-700 mr-2" data-id="element-1167">
                  ID: {plot.id}
                </span>
                <span className="mx-2 text-gray-300" data-id="element-1168">|</span>
                {language === 'bn' ? plot.districtBn : plot.district},{' '}
                {language === 'bn' ? plot.upazilaBn : plot.upazila}
              </p>
            </div>
          </div>
          <div className="flex space-x-3 w-full md:w-auto" data-id="element-1169">
            <button onClick={() => navigateTo('cloCertificate')} className="flex-1 md:flex-none px-6 py-2.5 bg-[#006A4E] text-white rounded-lg font-bold hover:bg-[#004D3A] transition-colors text-sm flex items-center justify-center shadow-sm" data-id="element-1170">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1171" /> {t('downloadCLO')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[500px]" data-id="element-1172">
        {/* Vertical Tabs (Desktop) / Horizontal Scroll (Mobile) */}
        <div className="w-full md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0" data-id="element-1173">
          <div className="flex md:flex-col overflow-x-auto md:overflow-visible hide-scrollbar p-2 md:p-4 gap-1" data-id="element-1174">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#006A4E] text-white shadow-md' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`} data-id="element-1175">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-green-100' : 'text-gray-400'}`} data-id="element-1176" />
                  <span data-id="element-1177">{tab.label}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-1178">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300" data-id="element-1179">
              <div className="space-y-8" data-id="element-1180">
                <div data-id="element-1181">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1182">
                    <MapPinIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1183" />
                    {t('locationInfo')}
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4" data-id="element-1184">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1185">
                      <div data-id="element-1186">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1187">
                          {t('district')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1188">
                          {language === 'bn' ? plot.districtBn : plot.district}
                        </p>
                      </div>
                      <div data-id="element-1189">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1190">
                          {t('upazila')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1191">
                          {language === 'bn' ? plot.upazilaBn : plot.upazila}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-1192">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1193">
                          {t('mouza')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1194">
                          {language === 'bn' ? plot.mouzaBn : plot.mouza}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div data-id="element-1195">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1196">
                    <FileTextIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1197" />
                    Record Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1198">
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100" data-id="element-1199">
                      <p className="text-xs text-blue-600 uppercase tracking-wider mb-1" data-id="element-1200">
                        {t('khatian')}
                      </p>
                      <p className="text-xl font-bold text-blue-900" data-id="element-1201">
                        {plot.khatian}
                      </p>
                    </div>
                    <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100" data-id="element-1202">
                      <p className="text-xs text-purple-600 uppercase tracking-wider mb-1" data-id="element-1203">
                        {t('dag')}
                      </p>
                      <p className="text-xl font-bold text-purple-900" data-id="element-1204">
                        {plot.dag}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8" data-id="element-1205">
                <div data-id="element-1206">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1207">
                    <ShieldCheckIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1208" />
                    Property Status
                  </h3>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-1209">
                    <div className="p-4 flex justify-between items-center" data-id="element-1210">
                      <span className="text-gray-600 font-medium" data-id="element-1211">
                        {t('landType')}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold capitalize" data-id="element-1212">
                        {t(plot.type as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1213">
                      <span className="text-gray-600 font-medium" data-id="element-1214">
                        {t('area')}
                      </span>
                      <span className="font-bold text-gray-900" data-id="element-1215">
                        {plot.area} Decimals
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1216">
                      <span className="text-gray-600 font-medium" data-id="element-1217">
                        {t('ownershipType')}
                      </span>
                      <span className="font-semibold text-gray-900 capitalize" data-id="element-1218">
                        {t(plot.ownership as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1219">
                      <span className="text-gray-600 font-medium" data-id="element-1220">
                        {t('mutationStatus')}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center ${plot.mutationStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1221">
                        {plot.mutationStatus === 'completed' && <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1222" />}
                        {t(plot.mutationStatus as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1223">
                      <span className="text-gray-600 font-medium" data-id="element-1224">
                        {t('taxStatus')}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plot.taxStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1225">
                        {t(plot.taxStatus as any)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* MUTATION INFO TAB */}
          {activeTab === 'mutation' && <div className="animate-in fade-in duration-300" data-id="element-1226">
              <div className="flex justify-between items-center mb-6" data-id="element-1227">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1228">
                  {t('tabMutation') || 'Mutation Info'}
                </h3>
                <button className="px-4 py-2 bg-[#006A4E] text-white rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-1229">
                  {t('applyMutation')}
                </button>
              </div>

              {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="space-y-6" data-id="element-1230">
                  {plot.transferHistory.map((record: any, idx: number) => <div key={record.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1231">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-1232">
                        <span className="font-bold text-gray-700 flex items-center" data-id="element-1233">
                          <FileTextIcon className="w-4 h-4 mr-2 text-[#006A4E]" data-id="element-1234" />
                          Record #{idx + 1}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1235">
                          {language === 'bn' ? record.typeBn : record.type}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1236">
                        <div data-id="element-1237">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1238">
                            {t('transferDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1239">
                            {record.date}
                          </p>
                        </div>
                        <div data-id="element-1240">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1241">
                            {t('transferFrom')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1242">
                            {record.from}
                          </p>
                        </div>
                        <div data-id="element-1243">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1244">
                            {t('transferTo')}
                          </p>
                          <p className="font-medium text-[#006A4E]" data-id="element-1245">
                            {record.to}
                          </p>
                        </div>
                        <div data-id="element-1246">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1247">
                            {t('transferType')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1248">
                            {language === 'bn' ? record.typeBn : record.type}
                          </p>
                        </div>
                        <div data-id="element-1249">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1250">
                            {t('mutCaseNo') || 'Case No.'}
                          </p>
                          <p className="font-mono font-medium text-gray-900" data-id="element-1251">
                            {record.caseNo || record.dolilNo || record.docRef}
                          </p>
                        </div>
                        <div data-id="element-1252">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1253">
                            {t('mutCaseDate') || 'Case Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1254">
                            {record.caseDate || record.dolilDate || record.date}
                          </p>
                        </div>
                        <div data-id="element-1255">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1256">
                            {t('approvalDate') || 'Approval Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1257">
                            {record.approvalDate || '-'}
                          </p>
                        </div>
                        <div data-id="element-1258">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1259">
                            {t('dcrNo') || 'DCR No.'}
                          </p>
                          <p className="font-mono font-medium text-gray-900" data-id="element-1260">
                            {record.dcrNo || '-'}
                          </p>
                        </div>
                        <div data-id="element-1261">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1262">
                            {t('dcrDate') || 'DCR Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1263">
                            {record.dcrDate || '-'}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1264">
                  <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1265" />
                  <p className="text-gray-500 font-medium" data-id="element-1266">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* TAX HISTORY TAB */}
          {activeTab === 'tax' && <div className="animate-in fade-in duration-300" data-id="element-1267">
              <div className="flex justify-between items-center mb-6" data-id="element-1268">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1269">
                  {t('ldTaxHistory')}
                </h3>
                <button className="px-4 py-2 bg-[#006A4E] text-white rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-1270">
                  {t('payTax')}
                </button>
              </div>

              {plot.taxHistory && plot.taxHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1271">
                  <div className="overflow-x-auto" data-id="element-1272">
                    <table className="w-full text-left text-sm" data-id="element-1273">
                      <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200" data-id="element-1274">
                        <tr data-id="element-1275">
                          <th className="px-6 py-4" data-id="element-1276">{t('taxYear')}</th>
                          <th className="px-6 py-4" data-id="element-1277">{t('amount')}</th>
                          <th className="px-6 py-4" data-id="element-1278">{t('status')}</th>
                          <th className="px-6 py-4" data-id="element-1279">{t('receiptNo')}</th>
                          <th className="px-6 py-4" data-id="element-1280">{t('paymentDate')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-1281">
                        {plot.taxHistory.map((record: any) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-1282">
                            <td className="px-6 py-4 font-medium text-gray-900" data-id="element-1283">
                              {record.year}
                            </td>
                            <td className="px-6 py-4 font-bold text-gray-700" data-id="element-1284">
                              {record.amount}
                            </td>
                            <td className="px-6 py-4" data-id="element-1285">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1286">
                                {t(record.status as any)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-500 font-mono text-xs" data-id="element-1287">
                              {record.receiptNo}
                            </td>
                            <td className="px-6 py-4 text-gray-500" data-id="element-1288">
                              {record.date}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1289">
                  <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1290" />
                  <p className="text-gray-500 font-medium" data-id="element-1291">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* PARCEL MAP TAB */}
          {activeTab === 'parcelMap' && <div className="animate-in fade-in duration-300" data-id="element-1292">
              <div className="flex justify-between items-center mb-6" data-id="element-1293">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1294">
                  {t('parcelMap') || 'Parcel Map'}
                </h3>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1295">
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 bg-white flex justify-center" data-id="element-1296">
                  <img src="https://cdn.magicpatterns.com/uploads/ny2k3HFhyHRGZ8XpcGKfEL/Parcel-map-data.jpg" alt="Parcel Map" className="max-w-full h-auto object-contain max-h-[600px]" data-id="element-1297" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-1298">
                  <div data-id="element-1299">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1300">
                      {t('khatian')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1301">
                      {plot.khatian}
                    </p>
                  </div>
                  <div data-id="element-1302">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1303">
                      {t('dag')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1304">
                      {plot.dag}
                    </p>
                  </div>
                  <div data-id="element-1305">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1306">
                      {t('mouza')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1307">
                      {language === 'bn' ? plot.mouzaBn : plot.mouza}
                    </p>
                  </div>
                  <div data-id="element-1308">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1309">
                      {t('area')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1310">
                      {plot.area} Decimals
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* TRANSFER HISTORY TAB */}
          {activeTab === 'transferHistory' && <div className="animate-in fade-in duration-300" data-id="element-1311">
              <div className="flex justify-between items-center mb-6" data-id="element-1312">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1313">
                  {t('tabTransferHistory') || 'Transfer History'}
                </h3>
              </div>

              {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1314">
                  <div className="bg-[#4CAF50] text-white text-center py-3 font-bold text-lg" data-id="element-1315">
                    {t('transferStatement') || 'হস্তান্তর বৃত্তান্ত'}
                  </div>
                  <div className="overflow-x-auto" data-id="element-1316">
                    <table className="w-full text-center text-sm border-collapse" data-id="element-1317">
                      <thead className="bg-gray-50 text-gray-800 font-medium" data-id="element-1318">
                        <tr data-id="element-1319">
                          <th className="px-4 py-3 border border-gray-200" data-id="element-1320">
                            {t('prevKhatianNo') || 'আগত খতিয়ান নং'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-1321">
                            {t('dag') || 'দাগ নং'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-1322">
                            {t('dagLandArea') || 'দাগে জমির পরিমান (একর)'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-1323">
                            {t('khatianLandArea') || 'খতিয়ানে জমির পরিমাণ (একর)'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-1324">
                            {t('transferredLandArea') || 'হস্তান্তরিত জমির পরিমাণ (একর)'}
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1325">
                        {plot.transferHistory.map((record: any, idx: number) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-1326">
                              <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900" data-id="element-1327">
                                {record.prevKhatianNo || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1328">
                                {record.dagNo || plot.dag}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1329">
                                {record.dagLandArea || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-1330">
                                {record.khatianLandArea || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 font-bold text-gray-900" data-id="element-1331">
                                {record.transferredLandArea || '-'}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="border-t border-gray-200" data-id="element-1332">
                    <div className="flex border-b border-gray-200" data-id="element-1333">
                      <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-1334">
                        {t('ownershipSource') || 'মালিকানা সূত্র'}
                      </div>
                      <div className="w-2/3 md:w-3/4 p-4 text-gray-900 font-medium flex items-center" data-id="element-1335">
                        {language === 'bn' ? (plot.transferHistory[0] as any).ownershipSourceBn || `${plot.transferHistory[0].typeBn} সূত্রেঃ` : (plot.transferHistory[0] as any).ownershipSource || `By ${plot.transferHistory[0].type}:`}
                      </div>
                    </div>
                    <div className="flex" data-id="element-1336">
                      <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-1337">
                        {t('descriptionLabel') || 'বিবরণ'}
                      </div>
                      <div className="w-2/3 md:w-3/4 p-4 text-gray-500 tracking-widest flex items-center" data-id="element-1338">
                        ------------------------
                      </div>
                    </div>
                  </div>
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1339">
                  <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1340" />
                  <p className="text-gray-500 font-medium" data-id="element-1341">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* LITIGATIONS / DISPUTES TAB */}
          {activeTab === 'disputes' && <div className="animate-in fade-in duration-300" data-id="element-1342">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1343">
                {t('litigations')}
              </h3>

              {plot.litigations && plot.litigations.length > 0 ? <div className="space-y-4" data-id="element-1344">
                  {plot.litigations.map((lit: any) => <div key={lit.id} className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1345">
                      <div className="bg-red-50 px-6 py-3 border-b border-red-100 flex justify-between items-center" data-id="element-1346">
                        <div className="flex items-center space-x-2" data-id="element-1347">
                          <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-1348" />
                          <span className="font-bold text-red-900" data-id="element-1349">
                            {lit.caseNo}
                          </span>
                        </div>
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold" data-id="element-1350">
                          {language === 'bn' ? lit.statusBn : lit.status}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1351">
                        <div data-id="element-1352">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-1353">
                            {t('court')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1354">
                            {language === 'bn' ? lit.courtBn : lit.court}
                          </p>
                        </div>
                        <div data-id="element-1355">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-1356">
                            {t('dateFiled')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1357">
                            {lit.dateFiled}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1358">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1359">
                            {t('plaintiff')}
                          </p>
                          <p className="font-bold text-gray-900" data-id="element-1360">
                            {language === 'bn' ? lit.plaintiffBn : lit.plaintiff}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-1361">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1362">
                            {t('defendant')}
                          </p>
                          <p className="font-bold text-gray-900" data-id="element-1363">
                            {language === 'bn' ? lit.defendantBn : lit.defendant}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-16 bg-green-50 rounded-xl border border-green-200" data-id="element-1364">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1365">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1366" />
                  </div>
                  <h4 className="text-lg font-bold text-green-900 mb-2" data-id="element-1367">
                    Clear Title
                  </h4>
                  <p className="text-green-700" data-id="element-1368">
                    No active litigations or disputes found for this property.
                  </p>
                </div>}
            </div>}

          {/* MORTGAGES TAB */}
          {activeTab === 'mortgages' && <div className="animate-in fade-in duration-300" data-id="element-1369">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1370">
                {t('mortgages')}
              </h3>

              {plot.mortgages && plot.mortgages.length > 0 ? <div className="space-y-4" data-id="element-1371">
                  {plot.mortgages.map((mortgage: any) => <div key={mortgage.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1372">
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1373">
                        <div className="flex items-center space-x-3" data-id="element-1374">
                          <BuildingIcon className="w-5 h-5 text-gray-500" data-id="element-1375" />
                          <span className="font-bold text-gray-900 text-lg" data-id="element-1376">
                            {language === 'bn' ? mortgage.bankBn : mortgage.bank}
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${mortgage.status === 'active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-1377">
                          {t(mortgage.status as any)}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-1378">
                        <div data-id="element-1379">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-1380">
                            {t('loanAmount')}
                          </p>
                          <p className="text-xl font-bold text-gray-900" data-id="element-1381">
                            {mortgage.amount}
                          </p>
                        </div>
                        <div data-id="element-1382">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-1383">
                            {t('mortgageDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1384">
                            {mortgage.date}
                          </p>
                        </div>
                        <div data-id="element-1385">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-1386">
                            {t('releaseDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1387">
                            {mortgage.releaseDate}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1388">
                  <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1389" />
                  <p className="text-gray-500 font-medium" data-id="element-1390">
                    No mortgage records found.
                  </p>
                </div>}
            </div>}

          {/* CLO TAB */}
          {activeTab === 'clo' && <div className="flex flex-col items-center justify-center py-16 space-y-6 animate-in fade-in duration-300" data-id="element-1391">
              <div className="w-32 h-32 bg-green-50 rounded-full flex items-center justify-center border-4 border-green-100" data-id="element-1392">
                <ShieldCheckIcon className="w-16 h-16 text-[#006A4E]" data-id="element-1393" />
              </div>
              <div className="text-center" data-id="element-1394">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1395">
                  Certificate of Land Ownership
                </h3>
                <p className="text-lg text-gray-500 font-mono bg-gray-100 px-4 py-1 rounded-md inline-block" data-id="element-1396">
                  {plot.cloId || 'Not Generated'}
                </p>
              </div>
              <button onClick={() => navigateTo('cloCertificate')} className="mt-4 px-8 py-3 bg-[#006A4E] text-white rounded-lg font-bold hover:bg-[#004D3A] transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-1397">
                View Full Certificate
              </button>
            </div>}

          {/* DOCUMENTS TAB */}
          {activeTab === 'documents' && <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1398">
              <div className="flex justify-between items-center" data-id="element-1399">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1400">
                  Related Documents
                </h3>
                <button onClick={handleUploadClick} className="px-4 py-2 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors text-sm flex items-center shadow-sm" data-id="element-1401">
                  <UploadIcon className="w-4 h-4 mr-2" data-id="element-1402" />
                  {t('uploadDoc')}
                </button>
              </div>

              {/* Upload UI */}
              {showUploadUI && <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center relative animate-in slide-in-from-top-4" data-id="element-1403">
                  <button onClick={() => setShowUploadUI(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-id="element-1404">
                    <XIcon className="w-5 h-5" data-id="element-1405" />
                  </button>

                  {uploadProgress > 0 ? <div className="max-w-md mx-auto" data-id="element-1406">
                      <FileIcon className="w-12 h-12 text-[#006A4E] mx-auto mb-4" data-id="element-1407" />
                      <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1408">
                        Uploading document...
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1409">
                        <div className="bg-[#006A4E] h-2.5 rounded-full transition-all duration-300" style={{
                  width: `${uploadProgress}%`
                }} data-id="element-1410"></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2" data-id="element-1411">
                        {uploadProgress}% Complete
                      </p>
                    </div> : <div data-id="element-1412">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-200" data-id="element-1413">
                        <UploadIcon className="w-8 h-8 text-[#006A4E]" data-id="element-1414" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1415">
                        Click to upload or drag and drop
                      </h4>
                      <p className="text-sm text-gray-500 mb-6" data-id="element-1416">
                        PDF, JPG, PNG up to 10MB
                      </p>

                      <label className="cursor-pointer px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm inline-block" data-id="element-1417">
                        Select File
                        <input type="file" className="hidden" onChange={simulateUpload} accept=".pdf,.jpg,.jpeg,.png" data-id="element-1418" />
                      </label>
                    </div>}
                </div>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1419">
                {uploadedFiles.map((doc, i) => <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-[#006A4E] cursor-pointer transition-all group" data-id="element-1420">
                    <div className="flex items-center space-x-4" data-id="element-1421">
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors" data-id="element-1422">
                        <FileTextIcon className="w-6 h-6 text-blue-600" data-id="element-1423" />
                      </div>
                      <div data-id="element-1424">
                        <p className="text-sm font-bold text-gray-900 group-hover:text-[#006A4E] transition-colors" data-id="element-1425">
                          {doc}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-1426">
                          Added on{' '}
                          {i === 0 && doc !== 'Original Deed.pdf' ? 'Just now' : 'Jan 15, 2026'}{' '}
                          • 2.4 MB
                        </p>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-[#006A4E] hover:bg-green-50 rounded-full transition-colors" data-id="element-1427">
                      <DownloadIcon className="w-5 h-5" data-id="element-1428" />
                    </button>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}